// Chrome for the district insights dashboard (v1).
//
// A frosted panel over the page background, plus concern-based navigation: each
// nav item is the QUESTION the page answers, with a short functional label. The shell is scope-agnostic — the container hands it whatever scope
// control applies (org picker for superadmins, nothing for org admins), so a
// future principal/teacher variant reuses it unchanged with different nouns.
import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import MainDashboard from "../../partials/Main";
import { INK, MUTED, GRID, SURFACE } from "../impact/tokens";

// Concern-labeled navigation. `to` is relative to the dashboard base route.
//
// Four items, matching the three Overview widgets (adoption / learning / robots)
// plus the digest. Schools ("who needs support?") and Engagement ("is ABii used
// enough?") are the two lenses of Adoption rather than tabs of their own: they are
// the same usage x impact join at different granularity -- the scatter plots reach
// against depth, the dose-response plots the same join binned by usage tier at
// learner level, and "engagement tier" is itself a breakdown dimension on the
// worklist. Split apart, the engagement half is a denominator report with the
// impact stripped out, which is the weaker page.
const NAV = [
  { to: "", exact: true, label: "Overview", sub: "At a glance" },
  { to: "/adoption", label: "Adoption", sub: "Usage, and is it working?" },
  { to: "/learning", label: "Learning", sub: "What are students learning?" },
  { to: "/fleet", label: "Fleet", sub: "Is the fleet right-sized?" },
];

// Two-line nav button: bold functional label over the muted concern question,
// with a chevron.
//
// It reads as a BUTTON, not as a chip. A 1px hairline box on a white fill sitting
// on a near-white panel is the treatment this dashboard gives a legend swatch and
// a disabled select — nothing about it says "this is the thing to click", and the
// concern questions underneath make it look even more like captioned text. Three
// cues avoid that, and it takes all three:
//
//   1. RAISE it. A drop shadow plus a warmer-than-panel white lifts the row off
//      the frosted surface, which is the only depth cue on the page.
//   2. A CHEVRON. One glyph that means "goes somewhere". Present at rest (muted),
//      not revealed on hover — a cue that only appears once you have already
//      guessed you can hover is not a cue.
//   3. HOVER. Border darkens to ink, the card lifts 1px, the question stops being
//      muted. Inline styles can't do :hover, so this is component state; the cost
//      is one rerender per pointer enter/leave on four elements.
//
// The active item keeps the solid-INK fill, which ui.js's SubTabs comment names as
// this dashboard's strongest selection signal and deliberately does not reuse.
const NavPill = ({ base, item, search }) => {
  const [hover, setHover] = useState(false);
  return (
    <NavLink
      to={{ pathname: `${base}${item.to}`, search }}
      exact={item.exact}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        // longhand border props (not the shorthand) so activeStyle's
        // borderColor override doesn't conflict on rerender
        borderWidth: 1,
        borderStyle: "solid",
        borderColor: hover ? INK : "#cdccc3",
        background: SURFACE,
        color: INK,
        borderRadius: 11,
        padding: "9px 13px 9px 15px",
        textDecoration: "none",
        minWidth: 132,
        boxShadow: hover
          ? "0 3px 10px rgba(43, 42, 40, 0.16)"
          : "0 1px 3px rgba(43, 42, 40, 0.10)",
        transform: hover ? "translateY(-1px)" : "none",
        transition: "box-shadow 120ms, transform 120ms, border-color 120ms",
      }}
      activeStyle={{
        background: INK,
        borderColor: INK,
        color: "#fff",
        boxShadow: "0 2px 8px rgba(43, 42, 40, 0.28)",
      }}
    >
      <span style={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <span style={{ fontSize: 14, fontWeight: 700, letterSpacing: 0.1 }}>
          {item.label}
        </span>
        <span style={{ fontSize: 11.5, opacity: hover ? 0.9 : 0.72 }}>
          {item.sub}
        </span>
      </span>
      {/* inherits `color`, so it is muted on a white pill and white on the
          active one without a second colour rule */}
      <span
        aria-hidden="true"
        style={{
          marginLeft: "auto",
          fontSize: 15,
          lineHeight: 1,
          opacity: hover ? 0.85 : 0.45,
        }}
      >
        ›
      </span>
    </NavLink>
  );
};

// Roster-provenance note for a reconstructed school year. Sits under the nav
// rather than on individual panels because every number on every page is
// roster-derived — repeating it per panel would be both noisier and less
// accurate about the scope of the caveat. Deliberately informational styling
// (no warning icon, no alarm color): the numbers are usable, they just have a
// known direction of error.
//
// Reserved for caveats of that shape — a known, page-wide direction of error.
// Something that makes a *subset* of numbers more accurate (the observed season
// start, say) belongs in the affected panels' PlotNote trays instead; putting it
// here would give an improvement the visual weight of a defect.
// `label` is the bold lead-in and keeps the roster default this was written for.
// Parameterised because the all-organizations scope raises a caveat of the same
// SHAPE -- page-wide, known direction of error, numbers still usable -- that
// would read as nonsense under a heading about a reconstructed year.
const CoverageNote = ({ text, label = "Reconstructed year" }) => (
  <div
    style={{
      display: "flex",
      gap: 8,
      alignItems: "baseline",
      padding: "9px 12px",
      marginBottom: 18,
      borderRadius: 10,
      border: `1px solid ${GRID}`,
      background: "rgba(255, 255, 255, 0.6)",
      color: MUTED,
      fontSize: 12,
      lineHeight: 1.45,
    }}
  >
    <span style={{ fontWeight: 700, whiteSpace: "nowrap" }}>{label}</span>
    <span>{text}</span>
  </div>
);

// Page frame: header row (title + scope/year controls), concern nav, content.
// `base` is the mount path (match.url) so nav links work wherever the
// dashboard is routed; the current query string rides along so scope
// (?organization=) survives navigation.
// `nav` defaults to the district dashboard's concern pages. A single-page
// container (the Classroom Matrix) passes `nav={[]}` and gets the same frame
// with no nav bar — the alternative was a second frame component that would
// drift from this one on the first styling change.
const DashShell = ({
  base,
  title,
  subtitle,
  controls,
  note,
  nav = NAV,
  children,
}) => {
  const { search } = useLocation();
  return (
    <div>
      <MainDashboard>
        <div
          style={{
            padding: 24,
            margin: "8px 4px 24px",
            borderRadius: 16,
            background: "rgba(251, 250, 247, 0.88)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            border: "1px solid rgba(255, 255, 255, 0.65)",
            boxShadow: "0 2px 10px rgba(43, 42, 40, 0.10)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
              flexWrap: "wrap",
              gap: 12,
              marginBottom: 14,
            }}
          >
            <div>
              <h1 style={{ margin: 0, fontSize: 22, color: INK }}>{title}</h1>
              {subtitle && (
                <div style={{ color: MUTED, fontSize: 13, marginTop: 2 }}>
                  {subtitle}
                </div>
              )}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              {controls}
            </div>
          </div>
          {/* A nav BAR, not a row of chips: the hairline underneath is the same
              device ControlBar uses to say "everything above this line chooses
              what you are looking at". */}
          {nav.length > 0 && (
          <nav
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              paddingBottom: 16,
              marginBottom: 18,
              borderBottom: `1px solid ${GRID}`,
            }}
          >
            {nav.map((item) => (
              <NavPill key={item.to} base={base} item={item} search={search} />
            ))}
          </nav>
          )}
          {/* A string for the one-caveat case this started as; an array of
              {label, text} when more than one applies at once -- a
              reconstructed year AND a cross-district scope are independent
              facts, and stacking them beats picking one. */}
          {(Array.isArray(note) ? note : note ? [{ text: note }] : []).map(
            (n, i) => (
              <CoverageNote key={i} text={n.text} label={n.label} />
            )
          )}
          {children}
        </div>
      </MainDashboard>
    </div>
  );
};

export { DashShell, CoverageNote, NAV };
